import { Component, computed, inject, input, signal } from '@angular/core';
import { DokaModal, DokaButton, DokaModalController, DokaSearchField, DokaIcon } from '@doka/angular-core';
import { I18NextEagerPipe } from 'angular-i18next';
import { IUserListItemModel, UsersClient } from 'src/api/services/generated/api-clients';
import { rxResource } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';

export type AssignmentLevel = 'division' | 'region' | 'country' | 'branch';

type AssignableUserOption = {
  id: string;
  primaryLabel: string;
  secondaryLabel: string | null;
  searchLabel: string;
  sortLabel: string;
};

@Component({
  selector: 'app-assign-user-modal',
  templateUrl: './assign-user-modal.component.html',
  imports: [DokaModal, DokaButton, I18NextEagerPipe, DokaSearchField, DokaIcon, SpinnerComponent],
})
export class AssignUserModalComponent {
  private readonly modalCtrl = inject(DokaModalController);
  private readonly usersClient = inject(UsersClient);

  public readonly level = input.required<AssignmentLevel>();
  public readonly locationId = input.required<string>();
  public readonly locationName = input.required<string>();
  public readonly existingUserIds = input.required<string[]>();

  protected readonly allUsers = rxResource({
    stream: () => this.usersClient.getAllUsers(),
  });

  protected readonly availableUsers = computed(() => {
    const users = this.allUsers.value()?.data ?? [];
    const existing = new Set(this.existingUserIds());
    return users.filter((u) => !existing.has(u.id));
  });

  protected readonly selectedUserId = signal<string | null>(null);
  protected readonly searchText = signal('');
  protected readonly submitted = signal(false);

  protected readonly visibleUsers = computed(() => {
    const search = this.searchText().trim().toLowerCase();
    const selectedUserId = this.selectedUserId();

    return this.availableUsers()
      .map((user) => this.toUserOption(user))
      .filter((user) => !search || user.searchLabel.includes(search))
      .sort((left, right) => {
        const selectedOrder = Number(right.id === selectedUserId) - Number(left.id === selectedUserId);
        if (selectedOrder !== 0) {
          return selectedOrder;
        }

        return left.sortLabel.localeCompare(right.sortLabel);
      });
  });

  protected selectUser(userId: string): void {
    this.selectedUserId.set(userId);
  }

  protected closeModal(): void {
    this.modalCtrl.dismiss();
  }

  protected async submit(): Promise<void> {
    this.submitted.set(true);
    const userId = this.selectedUserId();
    if (!userId) return;

    try {
      const locationId = this.locationId();
      switch (this.level()) {
        case 'division':
          await firstValueFrom(this.usersClient.assignUserToDivision(userId, locationId));
          break;
        case 'region':
          await firstValueFrom(this.usersClient.assignUserToRegion(userId, locationId));
          break;
        case 'country':
          await firstValueFrom(this.usersClient.assignUserToCountry(userId, locationId));
          break;
        case 'branch':
          await firstValueFrom(this.usersClient.assignUserToBranch(userId, locationId));
          break;
      }
      this.modalCtrl.dismiss(null, 'assigned');
    } catch (error) {
      console.error('Error assigning user:', error);
    }
  }

  private toUserOption(user: IUserListItemModel): AssignableUserOption {
    const primaryLabel = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username;
    const secondaryLabel = primaryLabel === user.username ? null : user.username;
    const searchLabel = `${primaryLabel} ${secondaryLabel ?? ''}`.trim().toLowerCase();
    const sortLabel = `${primaryLabel} ${user.username}`.trim().toLowerCase();

    return {
      id: user.id,
      primaryLabel,
      secondaryLabel,
      searchLabel,
      sortLabel,
    };
  }
}
